// AttachmentBriefingPanel.tsx — FE_SPEC §6.
//
// Right-side sticky panel that renders the 4-section attachment briefing.
//
// ★ FIX-C4 invariant: BE always returns a complete object · FE checks
// briefing.available === true before rendering. available=false → return null.
//
// ★ P0-1 闭环: content string is inlined in POST /sessions response
// (briefing.content) so this component does NOT fetch anything; it parses
// the markdown locally. The 4 sections live under fixed h2 headings:
// key_facts / key_data / contradictions / gaps.
//
// ★ P0-9 联动: quality_compromised → top yellow banner.
//
// Citation chips: every `[from attachment_X]` (and the variant
// `[from attachment_X §section]`) is rendered as <AttachmentCitationChip>;
// click bubbles via onCitationClick(attachmentId).

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttachmentBriefingDTO } from '@/ui/phases/PhaseProps';

interface AttachmentBriefingPanelProps {
  briefing: AttachmentBriefingDTO;
  onCitationClick?: (attachmentId: string) => void;
  defaultCollapsed?: boolean;
}

const SECTION_ORDER: ReadonlyArray<'key_facts' | 'key_data' | 'contradictions' | 'gaps'> = [
  'key_facts',
  'key_data',
  'contradictions',
  'gaps',
];

interface ParsedSection {
  key: (typeof SECTION_ORDER)[number];
  body: string;
}

// Cheap markdown parser: split on `## key_facts` / `## key_data` / etc.
function parseBriefing(content: string): ParsedSection[] {
  const out: ParsedSection[] = [];
  if (!content) return out;
  const lines = content.split('\n');
  let current: ParsedSection | null = null;
  for (const line of lines) {
    const headingMatch = line.match(/^##\s+(\w+)/);
    if (headingMatch) {
      const key = headingMatch[1] as ParsedSection['key'];
      if ((SECTION_ORDER as readonly string[]).includes(key)) {
        if (current) out.push(current);
        current = { key, body: '' };
        continue;
      }
    }
    if (current) {
      current.body += (current.body ? '\n' : '') + line;
    }
  }
  if (current) out.push(current);

  // Sort to canonical order, dropping anything not in the closed set.
  return SECTION_ORDER
    .map((key) => out.find((s) => s.key === key))
    .filter((s): s is ParsedSection => Boolean(s));
}

// Tokenize each line splitting on `[from attachment_X ...]` markers so the
// citation chips can be interleaved with the surrounding text.
const CITATION_RE = /\[from\s+(attachment_[A-Za-z0-9_]+)([^\]]*)\]/g;

function renderWithCitations(
  text: string,
  onClick?: (id: string) => void,
): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  CITATION_RE.lastIndex = 0;
  while ((m = CITATION_RE.exec(text))) {
    const start = m.index;
    if (start > last) nodes.push(text.slice(last, start));
    const id = m[1];
    const tail = (m[2] || '').trim();
    nodes.push(
      <button
        key={`${id}-${start}`}
        type="button"
        onClick={() => onClick?.(id)}
        data-testid="attachment-citation-chip"
        data-attachment-id={id}
        className="inline-flex items-center px-1.5 py-0.5 mx-0.5 text-[10px] font-mono uppercase tracking-meta border border-hairline rounded bg-canvas hover:bg-paper text-ink-2"
      >
        {id}
        {tail && <span className="ml-1 text-ink-3 normal-case">{tail}</span>}
      </button>,
    );
    last = start + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export default function AttachmentBriefingPanel({
  briefing,
  onCitationClick,
  defaultCollapsed = false,
}: AttachmentBriefingPanelProps) {
  const { t } = useTranslation();

  // ★ FIX-C4: never render the panel when briefing.available=false.
  // (Empty-state UI lives elsewhere; here we want a true no-render to keep
  //  the layout grid clean.)
  const sections = useMemo(
    () => parseBriefing(briefing?.content ?? ''),
    [briefing?.content],
  );

  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  if (!briefing || briefing.available !== true) return null;

  return (
    <aside
      data-testid="attachment-briefing-panel"
      className="border border-hairline rounded-md bg-paper"
    >
      <header className="flex items-center justify-between px-3 py-2 border-b border-hairline">
        <h3 className="font-serif text-sm text-ink">
          {t('attachment.briefing.title')}
        </h3>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          data-testid="briefing-toggle"
          className="text-xs font-mono text-ink-3 hover:text-ink"
        >
          {collapsed ? '+' : '−'}
        </button>
      </header>
      {briefing.quality_compromised && (
        <p
          data-testid="briefing-quality-compromised"
          role="alert"
          className="px-3 py-2 text-xs font-mono text-gold-700 bg-gold-100 border-b border-hairline"
        >
          {t('attachment.briefing.qualityCompromised')}
        </p>
      )}
      <div
        data-testid="briefing-body"
        data-collapsed={collapsed ? 'true' : 'false'}
        className={collapsed ? 'hidden' : 'px-3 py-2'}
      >
        {sections.map((s) => (
          <section
            key={s.key}
            data-testid={`briefing-section-${s.key}`}
            data-section={s.key}
            className="mb-3 last:mb-0"
          >
            <h4 className="font-mono text-[10px] uppercase tracking-meta text-ink-3 mb-1">
              {t(`attachment.briefing.section.${s.key}`)}
            </h4>
            <div className="text-sm text-ink whitespace-pre-wrap leading-relaxed">
              {s.body
                .split('\n')
                .filter((l) => l.trim() !== '')
                .map((line, i) => (
                  <div key={i}>{renderWithCitations(line, onCitationClick)}</div>
                ))}
            </div>
          </section>
        ))}
      </div>
    </aside>
  );
}
